import { useMemo, useRef, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { CheckCircle2, ListTodo, AlertTriangle, Zap, CalendarRange, Layers, FileDown } from "lucide-react";

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmt = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("tr-TR", { day: "numeric", month: "short" });

const Donut = ({ segments, total }) => {
  const R = 56, C = 2 * Math.PI * R;
  let acc = 0;
  return (
    <svg viewBox="0 0 140 140" className="w-40 h-40 -rotate-90">
      <circle cx="70" cy="70" r={R} fill="none" strokeWidth="16" className="stroke-slate-100 dark:stroke-white/10" />
      {segments.map((s, i) => {
        const frac = total ? s.value / total : 0;
        const el = (
          <circle key={i} cx="70" cy="70" r={R} fill="none" stroke={s.color} strokeWidth="16"
            strokeDasharray={`${frac * C} ${C}`} strokeDashoffset={-acc * C} />
        );
        acc += frac;
        return el;
      })}
    </svg>
  );
};

const TaskRow = ({ task, colMap, onClick, right }) => (
  <div onClick={onClick} data-testid="report-task-row"
    className="flex items-center gap-2.5 py-2 px-2.5 -mx-2.5 rounded-xl cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: colMap[task.column_id]?.color || "#8E8E93" }} />
    <span className="text-[13px] font-medium text-slate-700 dark:text-slate-300 truncate flex-1">{task.title}</span>
    {task.group && <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 dark:bg-white/10 rounded px-1.5 py-0.5 shrink-0">{task.group}</span>}
    <span className="text-[11px] text-slate-400 shrink-0">{right}</span>
  </div>
);

export default function ReportView({ board, onTaskClick, customer }) {
  const { columns, tasks } = board;
  const colMap = useMemo(() => Object.fromEntries(columns.map((c) => [c.id, c])), [columns]);
  const reportRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const exportPdf = async () => {
    setExporting(true);
    try {
      const isDark = document.documentElement.classList.contains("dark");
      const canvas = await html2canvas(reportRef.current, { scale: 2, backgroundColor: isDark ? "#161618" : "#F5F5F7", useCORS: true });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pw = 210, ph = 297;
      const ratio = canvas.height / canvas.width;
      let w = pw - 16, h = w * ratio;
      if (h > ph - 16) { h = ph - 16; w = h / ratio; }
      pdf.addImage(img, "PNG", (pw - w) / 2, 8, w, h);
      pdf.save(`${board.project.name.replace(/\s+/g, "-")}-rapor.pdf`);
    } catch (err) {
      toast.error("PDF oluşturulamadı: " + err.message);
    } finally {
      setExporting(false);
    }
  };

  const r = useMemo(() => {
    const today = todayISO();
    const doneIds = new Set(columns.filter((c) => c.is_done).map((c) => c.id));
    const done = tasks.filter((t) => doneIds.has(t.column_id));
    const open = tasks.filter((t) => !doneIds.has(t.column_id));
    const overdue = open.filter((t) => t.due_date && t.due_date < today)
      .sort((a, b) => a.due_date.localeCompare(b.due_date));
    const now = open.filter((t) => !overdue.includes(t) && t.start_date && t.start_date <= today)
      .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
    const future = open.filter((t) => t.start_date && t.start_date > today)
      .sort((a, b) => a.start_date.localeCompare(b.start_date));
    const pct = tasks.length ? Math.round((done.length / tasks.length) * 100) : 0;
    const byColumn = columns.map((c) => ({ ...c, count: tasks.filter((t) => t.column_id === c.id).length }));
    const groupNames = [...new Set(tasks.map((t) => t.group || "Genel"))];
    const byGroup = groupNames.map((name) => {
      const gt = tasks.filter((t) => (t.group || "Genel") === name);
      return { name, total: gt.length, done: gt.filter((t) => doneIds.has(t.column_id)).length };
    }).sort((a, b) => b.total - a.total);
    return { done, open, overdue, now, future, pct, byColumn, byGroup };
  }, [tasks, columns]);

  const kpis = [
    { label: "Toplam Task", value: tasks.length, icon: ListTodo, color: "#007AFF", sub: `${columns.length} kolon` },
    { label: "Tamamlanan", value: r.done.length, icon: CheckCircle2, color: "#34C759", sub: `%${r.pct} ilerleme` },
    { label: "Şu An Aktif", value: r.now.length, icon: Zap, color: "#FF9500", sub: "devam ediyor" },
    { label: "Geciken", value: r.overdue.length, icon: AlertTriangle, color: "#FF2D55", sub: r.overdue.length ? "aksiyon gerekli" : "her şey yolunda" },
  ];

  const card = "bg-white/90 dark:bg-[#1C1C1E]/90 backdrop-blur-md rounded-2xl border border-slate-200/70 dark:border-white/10 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]";

  return (
    <div data-testid="report-container" className="flex-1 overflow-y-auto pb-6">
      <div ref={reportRef} className="max-w-5xl mx-auto space-y-5 bg-[#F5F5F7] dark:bg-[#161618] p-1">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{customer?.name ? `${customer.name} · ` : ""}plena.pro</p>
            <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">{board.project.name} — Proje Raporu</h2>
            <p className="text-xs text-slate-400 mt-0.5">{new Date().toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}</p>
          </div>
          <button data-testid="report-export-pdf-button" data-html2canvas-ignore="true"
            onClick={exportPdf} disabled={exporting}
            className="h-10 px-4 rounded-xl bg-slate-900 hover:bg-slate-700 dark:bg-white/15 dark:hover:bg-white/25 active:scale-[0.98] text-white text-sm font-semibold transition-all flex items-center gap-2 shrink-0 disabled:opacity-60">
            {exporting ? <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> : <FileDown size={15} />}
            PDF İndir
          </button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map((k, i) => (
            <div key={k.label} data-testid="report-kpi-card" className={`${card} p-5 animate-fade-up`} style={{ animationDelay: `${i * 60}ms` }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: `${k.color}15`, color: k.color }}>
                <k.icon size={17} />
              </div>
              <p className="text-3xl font-semibold font-display tracking-tight text-slate-900 dark:text-white">{k.value}</p>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-0.5">{k.label}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">{k.sub}</p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-5 gap-5">
          <div data-testid="report-status-card" className={`${card} p-6 lg:col-span-2 animate-fade-up`}>
            <h3 className="font-display font-semibold tracking-tight text-slate-900 dark:text-white mb-1">Proje Durumu</h3>
            <p className="text-xs text-slate-400 mb-4">Kolonlara göre task dağılımı</p>
            <div className="relative flex items-center justify-center">
              <Donut segments={r.byColumn.map((c) => ({ value: c.count, color: c.color }))} total={tasks.length} />
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-3xl font-semibold font-display tracking-tight text-slate-900 dark:text-white">%{r.pct}</span>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">tamamlandı</span>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {r.byColumn.map((c) => (
                <div key={c.id} className="flex items-center gap-2.5 text-sm">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: c.color }} />
                  <span className="text-slate-600 dark:text-slate-300 flex-1">{c.name}</span>
                  <span className="font-semibold text-slate-800 dark:text-slate-100">{c.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3 space-y-5">
            {r.overdue.length > 0 && (
              <div data-testid="report-overdue-card" className={`${card} p-6 border-l-4 border-l-[#FF2D55] animate-fade-up`}>
                <h3 className="font-display font-semibold tracking-tight text-[#FF2D55] mb-2 flex items-center gap-2">
                  <AlertTriangle size={15} /> Geciken · {r.overdue.length}
                </h3>
                {r.overdue.map((t) => (
                  <TaskRow key={t.id} task={t} colMap={colMap} onClick={() => onTaskClick(t)} right={`${fmt(t.due_date)}'de bitmeliydi`} />
                ))}
              </div>
            )}
            <div data-testid="report-now-card" className={`${card} p-6 animate-fade-up`}>
              <h3 className="font-display font-semibold tracking-tight text-slate-900 dark:text-white mb-1 flex items-center gap-2">
                <Zap size={15} className="text-[#FF9500]" /> Şimdi
              </h3>
              <p className="text-xs text-slate-400 mb-2">Şu an üzerinde çalışılan tasklar</p>
              {r.now.length === 0 && <p className="text-sm text-slate-300 py-2">Şu an aktif task yok.</p>}
              {r.now.map((t) => (
                <TaskRow key={t.id} task={t} colMap={colMap} onClick={() => onTaskClick(t)} right={t.due_date ? `bitiş ${fmt(t.due_date)}` : ""} />
              ))}
            </div>
            <div data-testid="report-future-card" className={`${card} p-6 animate-fade-up`}>
              <h3 className="font-display font-semibold tracking-tight text-slate-900 dark:text-white mb-1 flex items-center gap-2">
                <CalendarRange size={15} className="text-[#007AFF]" /> Gelecek
              </h3>
              <p className="text-xs text-slate-400 mb-2">Planlanan tasklar</p>
              {r.future.length === 0 && <p className="text-sm text-slate-300 py-2">Planlanmış gelecek task yok.</p>}
              {r.future.map((t) => (
                <TaskRow key={t.id} task={t} colMap={colMap} onClick={() => onTaskClick(t)} right={`başlangıç ${fmt(t.start_date)}`} />
              ))}
            </div>
          </div>
        </div>

        <div data-testid="report-groups-card" className={`${card} p-6 animate-fade-up`}>
          <h3 className="font-display font-semibold tracking-tight text-slate-900 dark:text-white mb-1 flex items-center gap-2">
            <Layers size={15} className="text-slate-400" /> Grup Bazında İlerleme
          </h3>
          <p className="text-xs text-slate-400 mb-4">Ana grupların tamamlanma oranı</p>
          <div className="space-y-4">
            {r.byGroup.map((g) => {
              const p = g.total ? Math.round((g.done / g.total) * 100) : 0;
              return (
                <div key={g.name}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{g.name}</span>
                    <span className="text-xs text-slate-400">{g.done}/{g.total} · %{p}</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 dark:bg-white/10 overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-700 ${p === 100 ? "bg-[#34C759]" : "bg-[#007AFF]"}`} style={{ width: `${p}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
